import { forwardRef, memo, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { DesktopDatePicker } from '@mui/x-date-pickers';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { abortController } from '../../utils/abortController';
import useUser from '../../hooks/useUser';
import useSessionExpire from '../../hooks/useSessionExpire';
import TextField from '@mui/material/TextField';
import Grid from '@mui/material/Grid';
import Button from '@mui/material/Button';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import DeleteIcon from '@mui/icons-material/Delete';
import Modal from 'react-bootstrap/Modal';
import VirtualizedAutocomplete from '../../components/Autocomplete';
import AddBoxIcon from '@mui/icons-material/AddBox';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import Accordion from '@mui/material/Accordion';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DealSBLC from './DealSBLC';
import dayjs from 'dayjs';
import axios from 'axios';
import { isFieldLocked } from '../../utils/fieldUtils';
import { Tooltip, InputAdornment, IconButton } from "@mui/material";
import InfoIcon from '@mui/icons-material/Info';

dayjs.extend(isSameOrBefore);

const ShipmentDetails = forwardRef(({ isWritable = false, isDocumentWritable = false, isListDataLoading = false, isExistingShipmentDetails = false, isFormSavedOrDeleted = false, isFoodsOrPolymersDeal = false, isFoodsPolymersOrSBLCDeal = false, isSBLCDeal = false, isUpdated = false, selectedBusinessUnit = null, selectedDeal = null, isLinkedThroughRoute = false, isVantiraDeal = false, selectedPFI = null, selectedTranche = null, selectedTrancheForAutoPopulate = null, listData = {}, formData = {}, lockedFields = [], resetLockedFields = () => { }, sblcInitialFormData = {}, sblcFormData = {}, periodLockDate = null, isCurrentTrancheUnlocked = false, files = [], uploadedFiles = [], toggleListData = false, toggleDocumentList = false, toggleFileList = false, isCurrenTrancheANewTranche = false, expandedFormSections = [], setIsExistingShipmentDetails = () => { }, fetchRefreshedTranchesLists = () => { }, setIsFormUpdated = () => { }, setIsCurrenTrancheANewTranche = () => { }, setIsSBLCFormUpdated = () => { }, setIsFormSavedOrDeleted = () => { }, setTranches = () => { }, setSelectedTranche = () => { }, setSelectedDealForAutopopulate = () => { }, setSelectedPFIToAutoPopulate = () => { }, setSelectedTrancheForAutoPopulate = () => { }, setFormData = () => { }, setSBLCFormData = () => { }, setFiles = () => { }, setUploadedFiles = () => { }, setToggleListData = () => { }, setToggleDocumentList = () => { }, setToggleFileList = () => { }, setExpandedFormSections = () => { }, setShowDeleteModal = () => { }, setIsLoading = () => { }, setShowAlert = () => { }, setAlertMessage = () => { }, setAlertType = () => { }, handleChange = () => { }, logOut = () => { } }, ref) => {
  const { pathname } = useLocation();

  /* AUTHENTICATION VARIABLES */
  const currentUser = useUser();

  /* SESSION EXPIRY VARIABLES */
  const triggerSessionExpire = useSessionExpire();

  /* LIST VARIABLES */
  const {
    tranches = [],
    products = [],
    productGrades = [],
    seaPorts = [],
    shippingLines = []
  } = listData;
  const partialShipments = useMemo(() => ["Allowed", "Not Allowed"], []);

  /* FORM VARIABLES */
  const formRef = useRef(null);

  /* SBLC VARIABLES */
  const [isExistingSBLC, setIsExistingSBLC] = useState(false);

  /* ACCORDION VARIABLES */
  const [isProductOrProductGradeAccordionExpanded, setIsProductOrProductGradeAccordionExpanded] = useState(null);
  const [isPFIAccordionExpanded, setIsPFIAccordionExpanded] = useState(null);
  const [isProductGradeAccordionExpanded, setIsProductGradeAccordionExpanded] = useState(null);
  const [isCertificateAccodionExpanded, setIsCertificateAccodionExpanded] = useState(null);
  const productOrProductGradeAccordionRefs = useRef([]);
  const pfiAccordionRefs = useRef([]);
  const productGradeAccordionRefs = useRef([]);
  const certificateAccordionRefs = useRef([]);
  const prevProductGradesCountRef = useRef(formData?.tranche_product_grades_selected?.length || 0);
  const prevProductsCountRef = useRef(formData?.tranche_products_selected?.length || 0);
  const prevCertificateCountRef = useRef(formData?.Certificates_of_Origin?.length || 0);

  /* DIALOG VARIABLES */
  const [showWarningDialog, setShowWarningDialog] = useState(false);

  /* MODAL VARIABLES */
  const [showAddModal, setShowAddModal] = useState(false);

  // Determine if Commercial_Invoice_No should be editable
  const isCommercialInvoiceEditable = isVantiraDeal && isLinkedThroughRoute;

  useEffect(() => {
    const prevProductGradesCount = prevProductGradesCountRef.current;
    const prevProductsCount = prevProductsCountRef.current;
    const currentProductGradesCount = formData?.tranche_product_grades_selected?.length || 0;
    const currentProductsCount = formData?.tranche_products_selected?.length || 0;

    if (isFoodsOrPolymersDeal) {
      // Only expand if a new product grade is added
      if (currentProductGradesCount > prevProductGradesCount) {
        setIsProductOrProductGradeAccordionExpanded(currentProductGradesCount - 1);
      }
    } else {
      // Only expand if a new product is added
      if (currentProductsCount > prevProductsCount) {
        setIsProductOrProductGradeAccordionExpanded(currentProductsCount - 1);
      }
    }

    // Update previous counts
    prevProductGradesCountRef.current = currentProductGradesCount;
    prevProductsCountRef.current = currentProductsCount;
  }, [isFoodsOrPolymersDeal, formData?.tranche_products_selected, formData?.tranche_product_grades_selected]);

  useEffect(() => {
    const currentCount = formData?.Certificates_of_Origin?.length || 0;
    const prevCount = prevCertificateCountRef.current;

    // Only expand accordion if a new Certificate is added
    if (currentCount > prevCount) {
      setIsCertificateAccodionExpanded(currentCount - 1); // expand newly added Certificate
    }

    // Update previous count
    prevCertificateCountRef.current = currentCount;
  }, [formData?.Certificates_of_Origin]);

  useEffect(() => {
    if (isSBLCDeal) {
      // Check if the 'sblcFormData' is empty
      const isEmpty = typeof sblcFormData === "object" && !sblcFormData?.Deal_PFI_Id;

      // Set existing deal flags
      if (!isEmpty && !selectedTrancheForAutoPopulate) {
        setIsExistingSBLC(true);
      }
      else {
        setIsExistingSBLC(false);
      }

      if (!isFormSavedOrDeleted && !selectedTrancheForAutoPopulate) {
        const isSBLCFormUpdated = checkIfFormUpdated(sblcFormData, sblcInitialFormData);
        setIsSBLCFormUpdated(isSBLCFormUpdated);
      }
    }

    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [isSBLCDeal, sblcFormData, selectedTrancheForAutoPopulate]);

  useImperativeHandle(ref, () => ({
    isValid,
    handleSubmit: handleSubmit,
    handleDelete: handleDelete,
    handleAddTrancheWrapper: handleAddTrancheWrapper,
    handleCloseModal: handleCloseModal
  }));

  const checkIfFormUpdated = (formData, initialFormData, dateKeys = []) => {
    // Check non-date fields
    const isNonDateUpdated = Object.keys(initialFormData).some((key) => {
      const currentValue = formData[key];
      const initialValue = initialFormData[key];

      return JSON.stringify(currentValue) !== JSON.stringify(initialValue);
    });

    // Check date fields separately
    const isDateUpdated = dateKeys.some((dateKey) => {
      const currentDate = formData[dateKey] ? dayjs(formData[dateKey]) : formData[dateKey];
      const initialDate = initialFormData[dateKey] ? dayjs(initialFormData[dateKey]) : initialFormData[dateKey];

      return JSON.stringify(currentDate) !== JSON.stringify(initialDate);
    });

    return isNonDateUpdated || isDateUpdated;
  }

  const convertToDayJsObject = (date) => {
    if (!date) return null;

    const parsed = dayjs(date, ["MM/DD/YYYY", "YYYY-MM-DDTHH:mm:ss", "YYYY-MM-DD"], true);
    return parsed.isValid() ? parsed : null;
  };

  const isValid = () => {
    return formRef.current?.reportValidity();
  }

  const resetPostShipmentData = () => {
    const updatedTranches = tranches.filter(tranche => tranche.id !== selectedTranche?.id) || [];
    formRef?.current?.reset();

    if (updatedTranches.length > 0) {
      setSelectedTranche(updatedTranches[0]);
    }
    else {
      setSelectedTranche(null);
    }

    setTranches(updatedTranches);
    setFiles([]);
    setUploadedFiles([]);
    setIsExistingShipmentDetails(updatedTranches.length > 0);
    setIsFormSavedOrDeleted(false);
    setIsFormUpdated(false);
    setToggleListData(!toggleListData);
    setToggleDocumentList(!toggleDocumentList);
    setToggleFileList(!toggleFileList);
    setAlertMessage("Post-shipment data deleted successfully.");
    setAlertType("success");
    setShowAlert(true);
  }

  const handleSectionAccordionChange = (section) => (_, isExpanded) => {
    setExpandedFormSections(isExpanded ? [...expandedFormSections, section] : expandedFormSections.filter(id => id !== section));
  }

  const handleAccordionChange = (index, flag) => (_, isExpanded) => {
    if (flag === "Certificates") {
      setIsCertificateAccodionExpanded(isExpanded ? index : null);
    }
    else {
      setIsProductOrProductGradeAccordionExpanded(isExpanded ? index : null);
    }
  }

  const handleAddTrancheWrapper = () => {
    const isEmpty = Object.entries(formData)?.every(([key, value]) => value === "" || value === null || value === undefined) && uploadedFiles.length === 0;
    const isAnyFormFieldFilled = Object.values(formData).some((value) => {
      if (Array.isArray(value)) {
        return value.some((nestedObject) =>
          Object.values(nestedObject).some((nestedValue) =>
            Boolean(nestedValue)
          )
        );
      }

      return Boolean(value);
    });

    if (!isAnyFormFieldFilled) {
      setShowWarningDialog(true);
    }
    else if (!isEmpty && !isFormSavedOrDeleted && (!isExistingShipmentDetails || isUpdated)) {
      setShowAddModal(true);
    }
    else {
      handleAddTranche();
    }
  }

  const handleAddTranche = () => {
    setShowAddModal(false);

    formRef?.current?.reset();

    setIsExistingShipmentDetails(false);
    setIsExistingSBLC(false);
    setIsFormUpdated(false);
    setIsPFIAccordionExpanded(null);
    setIsProductGradeAccordionExpanded(null);
    setSelectedTranche(null);
    setFiles([]);
    setUploadedFiles([]);
  }

  const handleCertificateChange = (index, field, value) => {
    const newCertificates = [...formData?.Certificates_of_Origin];

    newCertificates[index] = { ...newCertificates[index], [field]: value };
    setFormData((previousFormData) => ({
      ...previousFormData,
      Certificates_of_Origin: newCertificates
    }));
  }

  const handleAddCertificate = (event) => {
    event.stopPropagation();

    const certificates = [...formData?.Certificates_of_Origin || []];
    const isAnyCertificateFieldFilled =
      certificates?.length > 0
        ? certificates.every((certificate) =>
          Object.values(certificate).some((value) => Boolean(value))
        )
        : true;

    if (isAnyCertificateFieldFilled) {
      const newCertificateData = {
        COO_Title: "",
        COO_Content: ""
      };
      const updatedCertificatesOfOrigin = [...certificates, newCertificateData];

      setFormData((previousFormData) => ({
        ...previousFormData,
        Certificates_of_Origin: updatedCertificatesOfOrigin
      }));
    }
    else {
      setShowWarningDialog(true);
    }
  }

  const handleDeleteCertificate = (event, index) => {
    event.stopPropagation();
    const newCertificates = [...formData?.Certificates_of_Origin];

    newCertificates.splice(index, 1);
    setFormData((previousFormData) => ({
      ...previousFormData,
      Certificates_of_Origin: newCertificates
    }));
  }

  const handleProductChange = (index, field, value) => {
    let formattedValue = value;
    if (field === "Shipped_Quantity") {
      if (formattedValue) {
        // Remove any non-numeric characters except the decimal point
        formattedValue = value.replace(/[^0-9.]/g, "");
        // Ensure the value has at most 3 decimal places
        if (formattedValue.includes(".")) {
          const [integerPart, decimalPart] = formattedValue.split(".");
          formattedValue = `${integerPart}.${decimalPart.substring(0, 3)}`;
        }
      } else {
        formattedValue = null;
      }
    }
    const data = { ...formData };
    const newProducts = [...data.tranche_products_selected];
    const updatedProduct = { ...newProducts[index] };
    // Get old quantity & price before update
    const oldQuantity = parseFloat(newProducts[index]?.Shipped_Quantity || 0);
    const oldPrice = parseFloat(newProducts[index]?.Deal_Product_Id?.Selling_Price || 0).toFixed(3);
    const oldAmount = (oldQuantity * oldPrice).toFixed(3);
    updatedProduct[field] = formattedValue;
    newProducts[index] = updatedProduct;
    // Get new quantity & price after update
    const newQuantity = parseFloat(updatedProduct?.Shipped_Quantity || 0);
    const newPrice = parseFloat(updatedProduct?.Deal_Product_Id?.Selling_Price || 0).toFixed(3);
    const newAmount = (newQuantity * newPrice).toFixed(3);
    // Adjust total by subtracting old and adding new
    const totalAmount = (parseFloat(data["Total_Amount"] || 0) - parseFloat(oldAmount) + parseFloat(newAmount)).toFixed(3);

    setFormData((previousFormData) => ({
      ...previousFormData,
      tranche_products_selected: newProducts,
      Total_Amount: parseFloat(totalAmount) // Ensure Total_Amount is a number with 3 decimal places
    }));
  };

  const handleAddProduct = (event) => {
    event.stopPropagation();

    const tranche_products_selected = [...formData?.tranche_products_selected];
    const isAnyProductFieldFilled =
      tranche_products_selected.length > 0
        ? tranche_products_selected.every((product) =>
          Object.values(product)?.some((value) =>
            Array.isArray(value) ? value.length > 0 : Boolean(value)
          )
        )
        : true;

    if (isAnyProductFieldFilled) {
      const newProductData = {
        Deal_Product_Id: null,
        Shipped_Quantity: null
      };

      setFormData((previousFormData) => ({
        ...previousFormData,
        tranche_products_selected: [
          ...(previousFormData.tranche_products_selected || []),
          newProductData
        ]
      }));
    }
    else {
      setShowWarningDialog(true);
    }
  }

  const handleDeleteProduct = (event, index) => {
    event.stopPropagation();
    setFormData((previousFormData) => {
      const productToDelete = previousFormData.tranche_products_selected[index];
      const deletedAmount = (
        parseFloat(productToDelete?.Shipped_Quantity || 0) *
        parseFloat(productToDelete?.Deal_Product_Id?.Selling_Price || 0)
      ).toFixed(3);
      return {
        ...previousFormData,
        tranche_products_selected: previousFormData.tranche_products_selected.filter((_, i) => i !== index),
        Total_Amount: (parseFloat(previousFormData.Total_Amount || 0) - parseFloat(deletedAmount)).toFixed(3)
      };
    });
  };

  const handleProductGradeChange = (index, field, value) => {
    let formattedValue = value;
    if (field === "Shipped_Quantity") {
      if (formattedValue) {
        // Remove any non-numeric characters except the decimal point
        formattedValue = value.replace(/[^0-9.]/g, "");
        // Ensure the value has at most 3 decimal places
        if (formattedValue.includes(".")) {
          const [integerPart, decimalPart] = formattedValue.split(".");
          formattedValue = `${integerPart}.${decimalPart.substring(0, 3)}`;
        }
      } else {
        formattedValue = null;
      }
    }
    const data = { ...formData };
    const newProductGrades = [...data.tranche_product_grades_selected];
    const updatedProductGrade = { ...newProductGrades[index] };
    // Get old quantity & price before update
    const oldQuantity = parseFloat(newProductGrades[index]?.Shipped_Quantity || 0);
    const oldPrice = parseFloat(newProductGrades[index]?.Deal_Product_Grade_Id?.Selling_Price || 0).toFixed(3);
    const oldAmount = (oldQuantity * oldPrice).toFixed(3);
    updatedProductGrade[field] = formattedValue;
    newProductGrades[index] = updatedProductGrade;
    // Get new quantity & price after update
    const newQuantity = parseFloat(updatedProductGrade?.Shipped_Quantity || 0);
    const newPrice = parseFloat(updatedProductGrade?.Deal_Product_Grade_Id?.Selling_Price || 0).toFixed(3);
    const newAmount = (newQuantity * newPrice).toFixed(3);
    // Adjust total by subtracting old and adding new
    const totalAmount = (parseFloat(data["Total_Amount"] || 0) - parseFloat(oldAmount) + parseFloat(newAmount)).toFixed(3);
    setFormData((previousFormData) => ({
      ...previousFormData,
      tranche_product_grades_selected: newProductGrades,
      Total_Amount: parseFloat(totalAmount) // Ensure Total_Amount is a number with 3 decimal places
    }));
  };
  const handleAddProductGrade = (event) => {
    event.stopPropagation();

    const tranche_product_grades_selected = [...formData?.tranche_product_grades_selected] || [];
    const isAnyProductGradeFieldFilled =
      tranche_product_grades_selected.length > 0
        ? tranche_product_grades_selected.every((product) =>
          Object.values(product)?.some((value) =>
            Array.isArray(value) ? value.length > 0 : Boolean(value)
          )
        )
        : true;

    if (isAnyProductGradeFieldFilled) {
      const newProductGradeData = {
        Deal_Product_Grade_Id: null,
        Shipped_Quantity: null
      };

      setFormData((previousFormData) => ({
        ...previousFormData,
        tranche_product_grades_selected: [
          ...(previousFormData.tranche_product_grades_selected || []),
          newProductGradeData
        ]
      }));
    }
    else {
      setShowWarningDialog(true);
    }
  }

  const handleDeleteProductGrade = (event, index) => {
    event.stopPropagation();
    setFormData((previousFormData) => {
      const productGradeToDelete = previousFormData.tranche_product_grades_selected[index];
      const deletedAmount = (
        parseFloat(productGradeToDelete?.Shipped_Quantity || 0) *
        parseFloat(productGradeToDelete?.Deal_Product_Grade_Id?.Selling_Price || 0)
      ).toFixed(3);
      return {
        ...previousFormData,
        tranche_product_grades_selected: previousFormData.tranche_product_grades_selected.filter((_, i) => i !== index),
        Total_Amount: (parseFloat(previousFormData.Total_Amount || 0) - parseFloat(deletedAmount)).toFixed(3)
      };
    });
  };

  const handleUpload = async (trancheId, resolve, reject) => {
    setShowAlert(false);
    setAlertMessage("");
    setAlertType("");

    const fileFormData = new FormData();
    const TrancheID = trancheId || selectedTranche?.value;

    uploadedFiles.forEach((file, index) => {
      const fileObj = {
        DealPFIId: selectedPFI?.id,
        TrancheID: TrancheID || selectedTranche?.id,
        user_id: currentUser?.id,
        fileName: file.name,
        docType: `Documentation - ${TrancheID}`,
        fileExtension: file.name.split(".").pop(),
        tabType: file.tabType,
        fileType: file.docType,
        markForApproval: file.markForApproval
      };

      fileFormData.append(`file${index}`, JSON.stringify(fileObj));
      fileFormData.append(`file${index}`, file);
    });

    fileFormData.append("pathname", pathname);

    await axios({
      method: "post",
      url: "/api/UploadDocument",
      data: fileFormData,
      signal: abortController.signal
    })
      .then((response) => {
        const { status, data } = response;

        if (status === 200) {
          setAlertMessage("Post-shipment data saved successfully.");
          setAlertType("success");
          setShowAlert(true);
          setUploadedFiles([])
          setToggleFileList(!toggleFileList);

          if (isDocumentWritable) {
            resolve(true);
          }
        }
        else if (status === 202) {
          setAlertMessage(data?.data || "An error occurred while processing your request. Please try again later or contact the site administrator.");
          setAlertType("error");
          setShowAlert(true);
          reject(false);
        }
        else {
          setAlertMessage("An error occurred while processing your request. Please try again later or contact the site administrator.");
          setAlertType("error");
          setShowAlert(true);
          reject(false);
        }
      })
      .catch((error) => {
        console.log("Upload Documentation Documents Api: ", error);

        if (axios.isCancel(error) || error.code === "ERR_CANCELED") {
          return;
        }

        const status = error?.response?.status;

        if (status === 403) {
          triggerSessionExpire();
        }
        else {
          setAlertMessage(
            status === 401
              ? "Unauthorized access. You do not have the required permissions to perform this action."
              : status === 429
                ? "Request limit exceeded. Your account is temporarily disabled. Please contact the site administrator."
                : "An error occurred while processing your request. Please try again later or contact the site administrator."
          );
          setAlertType("error");
          setShowAlert(true);

          if (status === 429) {
            setTimeout(logOut, 3000);
          }
        }

        reject(false);
      });
  }

  const handleDeleteFiles = async (fileIds) => {


    setIsLoading(true);
    setShowAlert(false);
    setAlertMessage("");
    setAlertType("");

    await axios({
      method: "post",
      url: "/api/DeleteDocument",
      data: {
        pathname: pathname,
        fileIds: fileIds
      },
      signal: abortController.signal
    })
      .then((response) => {
        setIsLoading(false);
        const { status } = response;

        if (status === 200) {
          resetPostShipmentData();
        }
        else {
          setAlertMessage("An error occurred while processing your request. Please try again later or contact the site administrator.");
          setAlertType("error");
          setShowAlert(true);
        }
      })
      .catch((error) => {
        console.log("Delete Documents Api: ", error);
        setIsLoading(false);

        if (axios.isCancel(error) || error.code === "ERR_CANCELED") {
          return;
        }

        const status = error?.response?.status;

        if (status === 403) {
          triggerSessionExpire();
        }
        else {
          setAlertMessage(
            status === 401
              ? "Unauthorized access. You do not have the required permissions to perform this action."
              : status === 429
                ? "Request limit exceeded. Your account is temporarily disabled. Please contact the site administrator."
                : "An error occurred while processing your request. Please try again later or contact the site administrator."
          );
          setAlertType("error");
          setShowAlert(true);

          if (status === 429) {
            setTimeout(logOut, 3000);
          }
        }
      });
  }

  // New function to save Commercial Invoice Number
  const saveCommercialInvoiceNumber = async (trancheId, commercialInvoiceNo) => {
    if (!isCommercialInvoiceEditable || !commercialInvoiceNo) {
      return Promise.resolve(true);
    }

    return axios({
      method: "post",
      url: "/api/SetTrancheCommercialInvoice",
      data: {
        Business_Unit_Id: selectedBusinessUnit,
        Tranche_Id: trancheId,
        Commercial_Invoice: commercialInvoiceNo
      },
      signal: abortController.signal
    })
      .then((response) => {
        const { status } = response;
        if (status === 200) {
          return true;
        } else {
          setAlertMessage("Failed to save Commercial Invoice Number.");
          setAlertType("error");
          setShowAlert(true);
          return false;
        }
      })
      .catch((error) => {
        console.log("Save Commercial Invoice Number Api: ", error);

        if (axios.isCancel(error) || error.code === "ERR_CANCELED") {
          return false;
        }

        const status = error?.response?.status;

        if (status === 403) {
          triggerSessionExpire();
        } else {
          setAlertMessage(
            status === 401
              ? "Unauthorized access. You do not have the required permissions to perform this action."
              : status === 429
                ? "Request limit exceeded. Your account is temporarily disabled. Please contact the site administrator."
                : "Failed to save Commercial Invoice Number. Please try again later or contact the site administrator."
          );
          setAlertType("error");
          setShowAlert(true);

          if (status === 429) {
            setTimeout(logOut, 3000);
          }
        }

        return false;
      });
  };

  const handleSubmit = async (event, isCurrentTrancheLocked, data, trancheID = null) => {
    event?.preventDefault();
    if (!isValid()) {
      return false;
    }

    return new Promise((resolve, reject) => {
      if (isExistingShipmentDetails && !isWritable && isDocumentWritable && uploadedFiles.length > 0) {
        // FIXED: Return the tranche ID even when only uploading files
        handleUpload(trancheID,
          (result) => resolve(trancheID || selectedTranche?.id), // Return tranche ID instead of true
          reject
        );
      }
      else {
        setShowAlert(false);
        setAlertMessage("");
        setAlertType("");

        // Date-only fields that must be sent without time component
        const dateOnlyFields = [
          "acceptance_date_to_customer",
          "acceptance_date_from_supplier",
          "Shipment_Date",
          "NN_Docs_Buyer",
          "NN_Docs_Supplier",
          "Commercial_Invoice_Date",
          "Delivery_Date",
          "Estimated_Arrival_to_POD",
          "SI_Date",
        ];

        const normalizedData = { ...data };
        dateOnlyFields.forEach((field) => {
          if (normalizedData[field]) {
            const parsed = dayjs(normalizedData[field]);
            normalizedData[field] = parsed.isValid()
              ? parsed.format("MM/DD/YYYY")
              : normalizedData[field];
          }
        });

        let updatedFormData = {
          ...normalizedData,
          Business_Unit_Id: selectedBusinessUnit,
          User_Id: currentUser?.id,
          ...(isExistingShipmentDetails && { Tranche_Id: trancheID })
        };

        if (updatedFormData?.Certificates_of_Origin?.length > 0 && selectedTrancheForAutoPopulate) {
          updatedFormData.Certificates_of_Origin = updatedFormData?.Certificates_of_Origin?.map(product => {
            const { COO_ID, ...rest } = product;
            return rest;
          });
        }

        if (isCurrenTrancheANewTranche) {
          const { Tranche_Id, tranche_ID, trnache_id, ...rest } = updatedFormData;
          updatedFormData = rest
        }
        const apiUrl = (isExistingShipmentDetails && trancheID)
          ? isFoodsPolymersOrSBLCDeal
            ? "/api/UpdatePostShipmentFoodsPolymersAndSBLC"
            : "/api/UpdatePostShipmentFertilizersAndPetroChemicals"
          : isFoodsPolymersOrSBLCDeal
            ? "/api/AddPostShipmentFoodsPolymersAndSBLC"
            : "/api/AddPostShipmentFertilizersAndPetroChemicals";

        let isSBLCSaved = Promise.resolve();

        updatedFormData = Object.fromEntries(Object.entries(updatedFormData).map(([key, value]) => [
          key,
          typeof value === "object" && value?.hasOwnProperty("value") ? value.id : value
        ]));

        if (isCurrentTrancheLocked) {
          updatedFormData = resetLockedFields(updatedFormData, lockedFields, "Shipment Details");
        }

        if (isSBLCDeal) {
          let updatedSBLCFormData = {
            ...data,
            Deal_PFI_Id: selectedPFI?.id,
            Tranche_Name: formData?.Tranche_Name,
            User_Id: currentUser?.id,
            ...(isExistingShipmentDetails && { Tranche_Id: trancheID })
          };
          const sblcApiUrl = isExistingSBLC ? "/api/UpdateDealTrancheSBLCProductGrades" : "/api/AddDealTrancheSBLCProductGrades";

          const transformObject = (obj, seen = new Set()) => {
            if (seen.has(obj)) return obj;
            if (typeof obj !== "object" || obj === null) return obj;

            seen.add(obj);

            if (Array.isArray(obj)) {
              return obj.map(item =>
                typeof item === "object" && item !== null
                  ? item.hasOwnProperty("value")
                    ? item.id
                    : transformObject(item, seen)
                  : item
              );
            }

            return Object.fromEntries(
              Object.entries(obj).map(([key, value]) => [
                key,
                typeof value === "object" && value !== null
                  ? Array.isArray(value)
                    ? transformObject(value, seen)
                    : value.hasOwnProperty("value")
                      ? value.id
                      : transformObject(value, seen)
                  : value
              ])
            );
          };

          if (isCurrentTrancheLocked) {
            updatedSBLCFormData = resetLockedFields(updatedSBLCFormData, lockedFields, "SBLC Product Grade");
          }

          updatedSBLCFormData = transformObject(updatedSBLCFormData);

          isSBLCSaved = axios({
            method: "post",
            url: sblcApiUrl,
            data: updatedSBLCFormData,
            signal: abortController.signal
          })
            .then((response) => {
              const { status, data } = response;

              if (status === 200) {
                if (!isExistingShipmentDetails) {
                  updatedFormData = { ...updatedFormData, Tranche_Id: data?.tranche_id };
                }

                setIsSBLCFormUpdated(false);
              }
              else if (status === 400) {
                setAlertMessage(data?.error || "An error occurred while processing your request. Please try again later or contact the site administrator.");
                setAlertType("error");
                setShowAlert(true);
                reject(false);
              }
              else {
                setAlertMessage("An error occurred while processing your request. Please try again later or contact the site administrator.");
                setAlertType("error");
                setShowAlert(true);
                reject(false);
              }
            })
            .catch((error) => {
              console.log("Add/Update Shipment Details SBLC Data Api: ", error);

              if (axios.isCancel(error) || error.code === "ERR_CANCELED") {
                return;
              }

              const status = error?.response?.status;

              if (status === 403) {
                triggerSessionExpire();
              }
              else {
                setAlertMessage(
                  status === 401
                    ? "Unauthorized access. You do not have the required permissions to perform this action."
                    : status === 429
                      ? "Request limit exceeded. Your account is temporarily disabled. Please contact the site administrator."
                      : "An error occurred while processing your request. Please try again later or contact the site administrator."
                );
                setAlertType("error");
                setShowAlert(true);

                if (status === 429) {
                  setTimeout(logOut, 3000);
                }
              }

              reject(false);
            });
        }

        isSBLCSaved
          .then(() =>
            axios.post(apiUrl, updatedFormData, { signal: abortController.signal })
              .then(async (response) => {
                const { status, data } = response;

                if (status === 200) {
                  const Tranche_Id = data?.Tranche_Id || trancheID;

                  if (isCommercialInvoiceEditable && updatedFormData?.is_vantira_deal && formData?.Commercial_Invoice_No) {
                    const invoiceSaved = await saveCommercialInvoiceNumber(
                      Tranche_Id,
                      formData.Commercial_Invoice_No
                    );

                    if (!invoiceSaved) {
                      console.log("Commercial Invoice Number save failed, but continuing...");
                    }
                  }

                  if (uploadedFiles.length > 0 && Tranche_Id) {
                    await handleUpload(Tranche_Id,
                      (result) => {
                        // After successful upload, resolve with Tranche_Id
                        setSelectedDealForAutopopulate(selectedDeal)
                        setSelectedPFIToAutoPopulate(selectedPFI)
                        setSelectedTrancheForAutoPopulate(null);
                        setUploadedFiles([]);
                        setIsExistingShipmentDetails(true);
                        setIsFormSavedOrDeleted(true);
                        setIsFormUpdated(false);
                        setIsCurrenTrancheANewTranche(false)
                        setToggleListData(!toggleListData);
                        setToggleDocumentList(!toggleDocumentList);
                        resolve(Tranche_Id); // FIXED: Return Tranche_Id instead of true
                      },
                      reject
                    );
                  } else {
                    // No files to upload, just set states and resolve with Tranche_Id
                    setSelectedDealForAutopopulate(selectedDeal)
                    setSelectedPFIToAutoPopulate(selectedPFI)
                    setSelectedTrancheForAutoPopulate(null);
                    setUploadedFiles([]);
                    setIsExistingShipmentDetails(true);
                    setIsFormSavedOrDeleted(true);
                    setIsFormUpdated(false);
                    setIsCurrenTrancheANewTranche(false)
                    setToggleListData(!toggleListData);
                    setToggleDocumentList(!toggleDocumentList);
                    resolve(Tranche_Id); // FIXED: Return Tranche_Id instead of true
                  }
                }
                else {
                  const errorMessage = status === 400
                    ? (data?.error || "An error occurred while processing your request. Please try again later or contact the site administrator.")
                    : "An error occurred while processing your request. Please try again later or contact the site administrator.";

                  setAlertMessage(errorMessage);
                  setAlertType("error");
                  setShowAlert(true);
                  reject(false);
                }
              })
          )
          .catch((error) => {
            console.log("Add/Update Deal Details Api: ", error);

            if (axios.isCancel(error) || error.code === "ERR_CANCELED") {
              return;
            }

            const status = error?.response?.status;

            if (status === 403) {
              triggerSessionExpire();
            }
            else {
              setAlertMessage(
                status === 401 ? "Unauthorized access. You do not have the required permissions." :
                  status === 429 ? "Request limit exceeded. Your account is temporarily disabled." :
                    "An error occurred while processing your request. Please try again later or contact the site administrator."
              );
              setAlertType("error");
              setShowAlert(true);

              if (status === 429) {
                setTimeout(logOut, 3000);
              }
            }

            reject(false);
          });
      }
    });
  }

  const handleDelete = async (passedPFI, passedTranche) => {
    setShowDeleteModal(false);
    setShowAlert(false);
    setAlertMessage("");
    setAlertType("");
    setIsLoading(true);

    let isCurrentTrancheLocked = false;

    if (
      selectedTranche?.hasOwnProperty('period_lock') &&
      selectedTranche.period_lock === 0
    ) {
      const currentTranche = await fetchRefreshedTranchesLists();
      isCurrentTrancheLocked = currentTranche?.period_lock;
    }
    if (isCurrentTrancheLocked) {
      setIsLoading(false);
      setAlertMessage("This tranche is locked and files cannot be deleted.");
      setAlertType("error");
      setShowAlert(true);
      return;
    }



    const fileIds = files.flatMap((file) => file?.file?.id ? [file.file.id] : []);

    if (isExistingShipmentDetails && !isWritable && isDocumentWritable && fileIds.length > 0) {
      handleDeleteFiles(fileIds);
    }
    else {
      let isSBLCDeleted = true;

      if (isSBLCDeal) {
        await axios({
          method: "post",
          url: "/api/DeleteDealTrancheSBLCProductGrades",
          data: {
            Deal_PFI_Id: passedPFI?.id,
            Tranche_Id: passedTranche?.id,
            SBLC_Id: sblcFormData?.SBLC_Id?.id
          },
          signal: abortController.signal
        })
          .then((response) => {
            const { status, data } = response;

            if (status === 200) {
              isSBLCDeleted = true;

              setIsExistingSBLC(false);
              setIsSBLCFormUpdated(false);
              setIsPFIAccordionExpanded(null);
              setIsProductGradeAccordionExpanded(null);
            }
            else if (status === 400) {
              setIsLoading(false);
              setAlertMessage(data?.error || "An error occurred while processing your request. Please try again later or contact the site administrator.");
              setAlertType("error");
              setShowAlert(true);
            }
            else {
              setIsLoading(false);
              setAlertMessage("An error occurred while processing your request. Please try again later or contact the site administrator.");
              setAlertType("error");
              setShowAlert(true);
            }
          })
          .catch((error) => {
            console.log("Delete Shipment Details SBLC Api: ", error);
            setIsLoading(false);

            if (axios.isCancel(error) || error.code === "ERR_CANCELED") {
              return;
            }

            const status = error?.response?.status;

            if (status === 403) {
              triggerSessionExpire();
            }
            else {
              setAlertMessage(
                status === 401
                  ? "Unauthorized access. You do not have the required permissions to perform this action."
                  : status === 429
                    ? "Request limit exceeded. Your account is temporarily disabled. Please contact the site administrator."
                    : "An error occurred while processing your request. Please try again later or contact the site administrator."
              );
              setAlertType("error");
              setShowAlert(true);

              if (status === 429) {
                setTimeout(logOut, 3000);
              }
            }
          });
      }

      if (isSBLCDeleted) {
        await axios({
          method: "post",
          url: "/api/DeletePostShipment",
          data: {
            Business_Unit_Id: selectedBusinessUnit,
            Deal_PFI_Id: passedPFI?.id,
            Tranche_Id: passedTranche?.id,
            User_Id: currentUser?.id
          },
          signal: abortController.signal
        })
          .then(async (response) => {
            const { status, data } = response;

            if (status === 200) {
              if (fileIds.length > 0) {
                handleDeleteFiles(fileIds);
              }
              else {
                setIsLoading(false);
                resetPostShipmentData();
              }
            }
            else if (status === 400) {
              setIsLoading(false);
              setAlertMessage(data?.error || "An error occurred while processing your request. Please try again later or contact the site administrator.");
              setAlertType("error");
              setShowAlert(true);
            }
            else {
              setIsLoading(false);
              setAlertMessage(data?.data);
              setAlertType("error");
              setShowAlert(true);
            }
          })
          .catch((error) => {
            console.log("Delete Shipment Details Data Api: ", error);
            setIsLoading(false);

            if (axios.isCancel(error) || error.code === "ERR_CANCELED") {
              return;
            }

            const status = error?.response?.status;

            if (status === 403) {
              triggerSessionExpire();
            }
            else {
              setAlertMessage(
                status === 401
                  ? "Unauthorized access. You do not have the required permissions to perform this action."
                  : status === 429
                    ? "Request limit exceeded. Your account is temporarily disabled. Please contact the site administrator."
                    : "An error occurred while processing your request. Please try again later or contact the site administrator."
              );
              setAlertType("error");
              setShowAlert(true);

              if (status === 429) {
                setTimeout(logOut, 3000);
              }
            }
          });
      }
    }
  }

  const handleCloseDialog = () => {
    setShowWarningDialog(false);
  }

  const handleCloseModal = () => {
    setShowDeleteModal(false);
    setShowAddModal(false);
  }

  const formSectionsHTML = [
    {
      id: 1,
      title: isFoodsPolymersOrSBLCDeal ? "Product Grades" : "Products",
      content: (
        <Grid size={{ xs: 12 }}>
          <Grid
            container
            spacing={3}
            className="align-items-start autocomplete"
          >
            {isSBLCDeal ? (
              <Grid size={{ xs: 12 }}>
                <DealSBLC
                  isWritable={isWritable}
                  isListDataLoading={isListDataLoading}
                  isPFIAccordionExpanded={isPFIAccordionExpanded}
                  isProductGradeAccordionExpanded={isProductGradeAccordionExpanded}
                  selectedTab="Shipment Details"
                  selectedPFI={selectedPFI?.id}
                  currentTranche={formData?.Tranche_Name}
                  sblcFormData={sblcFormData}
                  pfiAccordionRefs={pfiAccordionRefs}
                  productGradeAccordionRefs={productGradeAccordionRefs}
                  setShowAlert={setShowAlert}
                  setAlertMessage={setAlertMessage}
                  setAlertType={setAlertType}
                  setIsPFIAccordionExpanded={setIsPFIAccordionExpanded}
                  setIsProductGradeAccordionExpanded={setIsProductGradeAccordionExpanded}
                  setSBLCFormData={setSBLCFormData}
                  subTabName={"SBLC Product Grade"}
                  tabName={"Post-Shipment"}
                  isPeriodLocked={selectedTranche?.period_lock}
                  lockedFields={lockedFields}
                />
              </Grid>
            ) : (
              <Grid size={{ xs: 12 }}>
                {isFoodsOrPolymersDeal ? (
                  <>
                    {formData?.tranche_product_grades_selected?.map((productGrade, index) => {
                      const productGradeValue = productGrade?.Deal_Product_Grade_Id?.value || `Product Grade ${index + 1}`;

                      return (
                        <Accordion
                          ref={(element) => (productOrProductGradeAccordionRefs.current[index] = element)}
                          key={index}
                          expanded={isProductOrProductGradeAccordionExpanded === index}
                          onChange={handleAccordionChange(index, "Tranche Product Grades")}
                        >
                          <AccordionSummary
                            id={`product-grade-header-${index}`}
                            aria-controls={`product-grade-content-${index}`}
                            expandIcon={<ExpandMoreIcon />}
                            slot="div"
                          >
                            <small className="page-heading fw-bold item">
                              {productGradeValue}
                            </small>

                            <IconButton
                              color="error"
                              size="small"
                              className="pt-0"
                              disabled={!isWritable || selectedTranche?.period_lock}
                              onClick={(event) => handleDeleteProductGrade(event, index)}
                            >
                              <DeleteIcon fontSize="inherit" />
                            </IconButton>
                          </AccordionSummary>
                          <AccordionDetails>
                            <Grid
                              container
                              spacing={"10px"}
                              className="align-items-start autocomplete"
                            >
                              <Grid size={{ xs: 3 }}>
                                <VirtualizedAutocomplete
                                  isLoading={isListDataLoading}
                                  isMultiple={false}
                                  isObject={true}
                                  isRequired={true}
                                  isWritable={isWritable}
                                  isLocked={Boolean(selectedTranche?.period_lock && isFieldLocked("Deal_Product_Grade_Id", "Product Grade", "Post-Shipment", lockedFields))}
                                  filterOn="Product Grade"
                                  options={productGrades}
                                  selectedOptions={productGrade.Deal_Product_Grade_Id || null}
                                  handleSelectChange={(filterOn, newValue) => handleProductGradeChange(index, "Deal_Product_Grade_Id", newValue)}
                                />
                              </Grid>

                              <Grid size={{ xs: 3 }} className="form-text-field">
                                <TextField
                                  label="Shipped Quantity"
                                  id="Shipped_Quantity"
                                  type="number"
                                  disabled={(selectedTranche?.period_lock && isFieldLocked("Shipped_Quantity", "Shipment Details", "Post-Shipment", lockedFields))}
                                  value={productGrade.Shipped_Quantity || ""}
                                  onChange={(event) => handleProductGradeChange(index, "Shipped_Quantity", event.target.value)}
                                  size="small"
                                  slotProps={{
                                    input: {
                                      readOnly: !isWritable,
                                      min: 0,
                                      max: 2147483647,
                                      step: "0.001",
                                      endAdornment: (
                                        <InputAdornment position="end" sx={{ marginRight: "10px" }}>
                                          {Boolean((selectedTranche?.period_lock && isFieldLocked("Shipped_Quantity", "Shipment Details", "Post-Shipment", lockedFields))) && (
                                            <Tooltip
                                              title="This field is locked and cannot be edited due to period lock."
                                              arrow
                                              placement="top"
                                            >
                                              <InfoIcon
                                                fontSize="small"
                                                sx={{ cursor: 'default' }} // No margins
                                              />
                                            </Tooltip>
                                          )}
                                        </InputAdornment>
                                      ),
                                    },
                                  }}
                                  onKeyDown={(event) => {
                                    const invalidKeys = ["e", "E", "+", "-", "ArrowUp", "ArrowDown"];
                                    if (invalidKeys.includes(event.key)) {
                                      event.preventDefault();
                                    }
                                  }}
                                />
                              </Grid>
                            </Grid>
                          </AccordionDetails>
                        </Accordion>
                      );
                    })}
                  </>
                ) : (
                  <>
                    {formData?.tranche_products_selected?.map((product, index) => {
                      const productValue = product?.Deal_Product_Id?.value || `Product (${index + 1})`;

                      return (
                        <Accordion
                          ref={(element) => (productOrProductGradeAccordionRefs.current[index] = element)}
                          key={index}
                          expanded={isProductOrProductGradeAccordionExpanded === index}
                          onChange={handleAccordionChange(index, "Tranche Products")}
                        >
                          <AccordionSummary
                            id={`product-header-${index}`}
                            aria-controls={`product-content-${index}`}
                            expandIcon={<ExpandMoreIcon />}
                            slot="div"
                          >
                            <small className="page-heading fw-bold item">
                              {productValue}
                            </small>

                            <IconButton
                              color="error"
                              size="small"
                              className="pt-0"
                              disabled={!isWritable || selectedTranche?.period_lock}
                              onClick={(event) => handleDeleteProduct(event, index)}
                            >
                              <DeleteIcon fontSize="inherit" />
                            </IconButton>
                          </AccordionSummary>
                          <AccordionDetails>
                            <Grid
                              container
                              spacing={"10px"}
                              className="align-items-start autocomplete"
                            >
                              <Grid size={{ xs: 3 }}>
                                <VirtualizedAutocomplete
                                  isLoading={isListDataLoading}
                                  isMultiple={false}
                                  isObject={true}
                                  isRequired={true}
                                  isWritable={isWritable}
                                  isLocked={Boolean(selectedTranche?.period_lock && isFieldLocked("Deal_Product_Id", "Product", "Post-Shipment", lockedFields))}
                                  filterOn="Product"
                                  options={products}
                                  selectedOptions={product.Deal_Product_Id || null}
                                  handleSelectChange={(filterOn, newValue) => handleProductChange(index, "Deal_Product_Id", newValue)}
                                />
                              </Grid>

                              <Grid size={{ xs: 3 }} className="form-text-field">
                                <TextField
                                  label="Shipped Quantity"
                                  id="Shipped_Quantity"
                                  type="number"
                                  disabled={(selectedTranche?.period_lock && isFieldLocked("Shipped_Quantity", "Shipment Details", "Post-Shipment", lockedFields))}
                                  value={product.Shipped_Quantity || ""}
                                  onChange={(event) => handleProductChange(index, "Shipped_Quantity", event.target.value)}
                                  size="small"
                                  slotProps={{
                                    input: {
                                      readOnly: !isWritable,
                                      min: 0,
                                      max: 2147483647,
                                      step: "0.001",
                                      endAdornment: (
                                        <InputAdornment position="end" sx={{ marginRight: "10px" }}>
                                          {Boolean((selectedTranche?.period_lock && isFieldLocked("Shipped_Quantity", "Shipment Details", "Post-Shipment", lockedFields))) && (
                                            <Tooltip
                                              title="This field is locked and cannot be edited due to period lock."
                                              arrow
                                              placement="top"
                                            >
                                              <InfoIcon
                                                fontSize="small"
                                                sx={{ cursor: 'default' }} // No margins
                                              />
                                            </Tooltip>
                                          )}
                                        </InputAdornment>
                                      ),
                                    },
                                  }}
                                  onKeyDown={(event) => {
                                    const invalidKeys = ["e", "E", "+", "-", "ArrowUp", "ArrowDown"];
                                    if (invalidKeys.includes(event.key)) {
                                      event.preventDefault();
                                    }
                                  }}
                                />
                              </Grid>
                            </Grid>
                          </AccordionDetails>
                        </Accordion>
                      );
                    })}
                  </>
                )}
              </Grid>
            )}
          </Grid>
        </Grid>
      )
    },
    {
      id: 2,
      title: "Basic Information",
      content: (
        <Grid size={{ xs: 12 }}>
          <Grid
            container
            spacing={"10px"}
            className="align-items-start autocomplete"
          >
            <Grid size={{ xs: 3 }}>
              <VirtualizedAutocomplete
                isLoading={isListDataLoading}
                isMultiple={false}
                isObject={true}
                isWritable={isWritable}
                isLocked={Boolean(selectedTranche?.period_lock && isFieldLocked("Port_Of_Loading_Id", "Shipment Details", "Post-Shipment", lockedFields))}
                filterOn="Port of Loading"
                options={seaPorts}
                selectedOptions={formData?.Port_Of_Loading_Id || null}
                handleSelectChange={(filterOn, newValue) => handleChange("Port_Of_Loading_Id", newValue)}
              />
            </Grid>

            <Grid size={{ xs: 3 }}>
              <VirtualizedAutocomplete
                isLoading={isListDataLoading}
                isMultiple={false}
                isObject={true}
                isWritable={isWritable}
                isLocked={Boolean(selectedTranche?.period_lock && isFieldLocked("Port_Of_Discharge_Id", "Shipment Details", "Post-Shipment", lockedFields))}
                filterOn="Port of Discharge"
                options={seaPorts}
                selectedOptions={formData?.Port_Of_Discharge_Id || null}
                handleSelectChange={(filterOn, newValue) => handleChange("Port_Of_Discharge_Id", newValue)}
              />
            </Grid>

            <Grid size={{ xs: 3 }} className="form-text-field">
              <TextField
                label="Commercial Invoice No."
                id="Commercial_Invoice_No"
                disabled={
                  (!isCommercialInvoiceEditable && (selectedTranche?.period_lock && isFieldLocked("Commercial_Invoice_No", "Shipment Details", "Post-Shipment", lockedFields))) ||
                  (!formData?.Commercial_Invoice_No && isCommercialInvoiceEditable)
                }
                value={formData?.Commercial_Invoice_No || ""}
                onChange={(event) => handleChange("Commercial_Invoice_No", event.target.value)}
                size="small"
                slotProps={{
                  input: {
                    readOnly: !isWritable || !isCommercialInvoiceEditable || !formData?.Commercial_Invoice_No,
                    endAdornment: (
                      <InputAdornment position="end" sx={{ marginRight: "10px" }}>
                        {Boolean((selectedTranche?.period_lock && isFieldLocked("Commercial_Invoice_No", "Shipment Details", "Post-Shipment", lockedFields))) && (
                          <Tooltip
                            title="This field is locked and cannot be edited due to period lock."
                            arrow
                            placement="top"
                          >
                            <InfoIcon
                              fontSize="small"
                              sx={{ cursor: 'default' }}
                            />
                          </Tooltip>
                        )}
                        {!formData?.Commercial_Invoice_No && isCommercialInvoiceEditable && (
                          <Tooltip
                            title="Please generate CI document first to edit Commercial Invoice No."
                            arrow
                            placement="top"
                          >
                            <InfoIcon
                              fontSize="small"
                              sx={{ cursor: 'default' }} // No margins
                            />
                          </Tooltip>
                        )}
                      </InputAdornment>
                    ),
                  },
                }}
              />
            </Grid>

            <Grid size={{ xs: 3 }} className="form-text-field">
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DesktopDatePicker
                  inputFormat="MM/DD/YYYY"
                  label="Document Creation Date"
                  disableMaskedInput
                  readOnly={!isWritable || (selectedTranche?.period_lock && isFieldLocked("Commercial_Invoice_Date", "Shipment Details", "Post-Shipment", lockedFields))}
                  className="date-picker w-100"
                  value={convertToDayJsObject(formData?.Commercial_Invoice_Date)}
                  onChange={(newDate) => handleChange("Commercial_Invoice_Date", newDate, true)}
                  onAccept={(newDate) => handleChange("Commercial_Invoice_Date", newDate, true)}
                  enableAccessibleFieldDOMStructure={false}
                  slots={{
                    textField: (params) => (
                      <TextField
                        {...params}
                        size="small"
                        fullWidth
                        InputProps={{
                          ...params.InputProps,
                          disabled: true,
                          endAdornment: (
                            <>
                              {Boolean((selectedTranche?.period_lock && isFieldLocked("Commercial_Invoice_Date", "Shipment Details", "Post-Shipment", lockedFields))) && (
                                <Tooltip
                                  title="This field is locked and cannot be edited due to period lock."
                                  arrow
                                  placement="top"
                                >
                                  <InfoIcon
                                    fontSize="small"
                                    className='MuiSvgIcon-info'
                                    sx={{ marginRight: "0", cursor: 'default' }}
                                  />
                                </Tooltip>
                              )}
                              {params.InputProps?.endAdornment}
                            </>
                          ),
                        }}
                      />
                    ),
                  }}
                  slotProps={{
                    actionBar: {
                      actions: ["clear"]
                    }
                  }}
                />
              </LocalizationProvider>
            </Grid>

            <Grid size={{ xs: 3 }}>
              <VirtualizedAutocomplete
                isLoading={isListDataLoading}
                isMultiple={false}
                isObject={true}
                isWritable={isWritable}
                isLocked={Boolean(selectedTranche?.period_lock && isFieldLocked("Shipping_Line", "Shipment Details", "Post-Shipment", lockedFields))}
                filterOn="Shipping Line"
                options={shippingLines}
                selectedOptions={formData?.Shipping_Line || null}
                handleSelectChange={(filterOn, newValue) => handleChange("Shipping_Line", newValue)}
              />
            </Grid>

            <Grid size={{ xs: 3 }} className="form-text-field">
              <TextField
                label="Vessel Name"
                id="Vessel_Name"
                value={formData?.Vessel_Name || ""}
                disabled={(selectedTranche?.period_lock && isFieldLocked("Vessel_Name", "Shipment Details", "Post-Shipment", lockedFields))}
                onChange={(event) => handleChange("Vessel_Name", event.target.value)}
                size="small"
                slotProps={{
                  input: {
                    readOnly: !isWritable,
                    endAdornment: (
                      <InputAdornment position="end" sx={{ marginRight: "10px" }}>
                        {Boolean((selectedTranche?.period_lock && isFieldLocked("Vessel_Name", "Shipment Details", "Post-Shipment", lockedFields))) && (
                          <Tooltip
                            title="This field is locked and cannot be edited due to period lock."
                            arrow
                            placement="top"
                          >
                            <InfoIcon
                              fontSize="small"
                              sx={{ cursor: 'default' }} // No margins
                            />
                          </Tooltip>
                        )}
                      </InputAdornment>
                    ),
                  },
                }}
              />
            </Grid>

            <Grid size={{ xs: 3 }} className="form-text-field">
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DesktopDatePicker
                  inputFormat="MM/DD/YYYY"
                  label="NN Docs Supplier"
                  disableMaskedInput
                  readOnly={!isWritable || (selectedTranche?.period_lock && isFieldLocked("NN_Docs_Supplier", "Shipment Details", "Post-Shipment", lockedFields))}
                  className="date-picker w-100"
                  value={convertToDayJsObject(formData?.NN_Docs_Supplier)}
                  onChange={(newDate) => handleChange("NN_Docs_Supplier", newDate, true)}
                  onAccept={(newDate) => handleChange("NN_Docs_Supplier", newDate, true)}
                  enableAccessibleFieldDOMStructure={false}
                  slots={{
                    textField: (params) => (
                      <TextField
                        {...params}
                        size="small"
                        fullWidth
                        InputProps={{
                          ...params.InputProps,
                          disabled: true,
                          endAdornment: (
                            <>
                              {Boolean((selectedTranche?.period_lock && isFieldLocked("NN_Docs_Supplier", "Shipment Details", "Post-Shipment", lockedFields))) && (
                                <Tooltip
                                  title="This field is locked and cannot be edited due to period lock."
                                  arrow
                                  placement="top"
                                >
                                  <InfoIcon
                                    fontSize="small"
                                    className='MuiSvgIcon-info'
                                    sx={{ marginRight: "0", cursor: 'default' }}
                                  />
                                </Tooltip>
                              )}
                              {params.InputProps?.endAdornment}
                            </>
                          ),
                        }}
                      />
                    ),
                  }}
                  slotProps={{
                    actionBar: {
                      actions: ["clear"]
                    }
                  }}
                />
              </LocalizationProvider>
            </Grid>

            <Grid size={{ xs: 3 }} className="form-text-field">
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DesktopDatePicker
                  inputFormat="MM/DD/YYYY"
                  label="NN Docs Buyer"
                  className="date-picker w-100"
                  disableMaskedInput
                  readOnly={!isWritable || (selectedTranche?.period_lock && isFieldLocked("NN_Docs_Buyer", "Shipment Details", "Post-Shipment", lockedFields))}
                  value={convertToDayJsObject(formData?.NN_Docs_Buyer)}
                  onChange={(newDate) => handleChange("NN_Docs_Buyer", newDate, true)}
                  onAccept={(newDate) => handleChange("NN_Docs_Buyer", newDate, true)}
                  enableAccessibleFieldDOMStructure={false}
                  slots={{
                    textField: (params) => (
                      <TextField
                        {...params}
                        size="small"
                        fullWidth
                        InputProps={{
                          ...params.InputProps,
                          disabled: true,
                          endAdornment: (
                            <>
                              {Boolean((selectedTranche?.period_lock && isFieldLocked("NN_Docs_Buyer", "Shipment Details", "Post-Shipment", lockedFields))) && (
                                <Tooltip
                                  title="This field is locked and cannot be edited due to period lock."
                                  arrow
                                  placement="top"
                                >
                                  <InfoIcon
                                    fontSize="small"
                                    className='MuiSvgIcon-info'
                                    sx={{ marginRight: "0", cursor: 'default' }}
                                  />
                                </Tooltip>
                              )}
                              {params.InputProps?.endAdornment}
                            </>
                          ),
                        }}
                      />
                    ),
                  }}
                  slotProps={{
                    actionBar: {
                      actions: ["clear"]
                    }
                  }}
                />
              </LocalizationProvider>
            </Grid>

            <Grid size={{ xs: 3 }} className="form-text-field">
              <TextField
                label="Voyage No."
                id="Voyage_No"
                value={formData?.Voyage_No || ""}
                disabled={(selectedTranche?.period_lock && isFieldLocked("Voyage_No", "Shipment Details", "Post-Shipment", lockedFields))}
                onChange={(event) => handleChange("Voyage_No", event.target.value)}
                size="small"
                slotProps={{
                  input: {
                    readOnly: !isWritable,
                    endAdornment: (
                      <InputAdornment position="end" sx={{ marginRight: "10px" }}>
                        {Boolean((selectedTranche?.period_lock && isFieldLocked("Voyage_No", "Shipment Details", "Post-Shipment", lockedFields))) && (
                          <Tooltip
                            title="This field is locked and cannot be edited due to period lock."
                            arrow
                            placement="top"
                          >
                            <InfoIcon
                              fontSize="small"
                              sx={{ cursor: 'default' }} // No margins
                            />
                          </Tooltip>
                        )}
                      </InputAdornment>
                    ),
                  },
                }}
              />
            </Grid>

            <Grid size={{ xs: 3 }} className="form-text-field">
              <TextField
                label="Master BL Number."
                id="Master_Bill_of_Lading_No"
                value={formData?.Master_Bill_of_Lading_No || ""}
                disabled={(selectedTranche?.period_lock && isFieldLocked("Master_Bill_of_Lading_No", "Shipment Details", "Post-Shipment", lockedFields))}
                onChange={(event) => handleChange("Master_Bill_of_Lading_No", event.target.value)}
                size="small"
                slotProps={{
                  input: {
                    readOnly: !isWritable,
                    endAdornment: (
                      <InputAdornment position="end" sx={{ marginRight: "10px" }}>
                        {Boolean((selectedTranche?.period_lock && isFieldLocked("Master_Bill_of_Lading_No", "Shipment Details", "Post-Shipment", lockedFields))) && (
                          <Tooltip
                            title="This field is locked and cannot be edited due to period lock."
                            arrow
                            placement="top"
                          >
                            <InfoIcon
                              fontSize="small"
                              sx={{ cursor: 'default' }} // No margins
                            />
                          </Tooltip>
                        )}
                      </InputAdornment>
                    ),
                  },
                }}
              />
            </Grid>

            <Grid size={{ xs: 3 }} className="form-text-field">
              <TextField
                label="NN Documents Status - Customer"
                id="Documents_Status_to_Customer"
                value={formData?.Documents_Status_to_Customer || ""}
                disabled={(selectedTranche?.period_lock && isFieldLocked("Documents_Status_to_Customer", "Shipment Details", "Post-Shipment", lockedFields))}
                onChange={(event) => handleChange("Documents_Status_to_Customer", event.target.value)}
                size="small"
                slotProps={{
                  input: {
                    readOnly: !isWritable,
                    endAdornment: (
                      <InputAdornment position="end" sx={{ marginRight: "10px" }}>
                        {Boolean((selectedTranche?.period_lock && isFieldLocked("Documents_Status_to_Customer", "Shipment Details", "Post-Shipment", lockedFields))) && (
                          <Tooltip
                            title="This field is locked and cannot be edited due to period lock."
                            arrow
                            placement="top"
                          >
                            <InfoIcon
                              fontSize="small"
                              sx={{ cursor: 'default' }} // No margins
                            />
                          </Tooltip>
                        )}
                      </InputAdornment>
                    ),
                  },
                }}
              />
            </Grid>

            <Grid size={{ xs: 3 }} className="form-text-field">
              <TextField
                label="NN Documents Status - Supplier"
                id="Documents_Status_to_Supplier"
                value={formData?.Documents_Status_to_Supplier || ""}
                disabled={(selectedTranche?.period_lock && isFieldLocked("Documents_Status_to_Supplier", "Shipment Details", "Post-Shipment", lockedFields))}
                onChange={(event) => handleChange("Documents_Status_to_Supplier", event.target.value)}
                size="small"
                slotProps={{
                  input: {
                    readOnly: !isWritable,
                    endAdornment: (
                      <InputAdornment position="end" sx={{ marginRight: "10px" }}>
                        {Boolean((selectedTranche?.period_lock && isFieldLocked("Documents_Status_to_Supplier", "Shipment Details", "Post-Shipment", lockedFields))) && (
                          <Tooltip
                            title="This field is locked and cannot be edited due to period lock."
                            arrow
                            placement="top"
                          >
                            <InfoIcon
                              fontSize="small"
                              sx={{ cursor: 'default' }} // No margins
                            />
                          </Tooltip>
                        )}
                      </InputAdornment>
                    ),
                  },
                }}
              />
            </Grid>

            <Grid size={{ xs: 3 }}>
              <VirtualizedAutocomplete
                isLoading={isListDataLoading}
                isMultiple={false}
                isObject={false}
                isWritable={isWritable}
                isLocked={Boolean(selectedTranche?.period_lock && isFieldLocked("Transport_Reference_Value", "Shipment Details", "Post-Shipment", lockedFields))}
                filterOn="Transport Reference Value"
                options={["Bill of Lading No.", "Truck Receipt No.", "Airway Bill No.", "Transport Reference"]}
                selectedOptions={formData?.Transport_Reference_Value || null}
                handleSelectChange={(filterOn, newValue) => {
                  handleChange("Transport_Reference_Value", newValue);
                }}
              />
            </Grid>

            {formData?.Transport_Reference_Value && (
              <Grid size={{ xs: 3 }} className="form-text-field">
                <TextField
                  label={formData.Transport_Reference_Value}
                  id="transport_reference"
                  value={
                    formData.Transport_Reference_Value === "Bill of Lading No."
                      ? (formData.Split_Bill_of_Lading_No || "")
                      : formData.Transport_Reference_Value === "Truck Receipt No."
                        ? (formData.Truck_Receipt_No || "")
                        : formData.Transport_Reference_Value === "Airway Bill No."
                          ? (formData.Airway_Bill_No || "")
                          : (formData.Transport_Reference || "")
                  }
                  disabled={(selectedTranche?.period_lock && isFieldLocked(
                    formData.Transport_Reference_Value === "Bill of Lading No."
                      ? "Split_Bill_of_Lading_No"
                      : formData.Transport_Reference_Value === "Truck Receipt No."
                        ? "Truck_Receipt_No"
                        : formData.Transport_Reference_Value === "Airway Bill No."
                          ? "Airway_Bill_No"
                          : "Transport_Reference",
                    "Shipment Details",
                    "Post-Shipment",
                    lockedFields
                  ))}
                  onChange={(event) => {
                    const fieldName = formData.Transport_Reference_Value === "Bill of Lading No."  // ✅ was Transport_Reference
                      ? "Split_Bill_of_Lading_No"
                      : formData.Transport_Reference_Value === "Truck Receipt No."                 // ✅ was Transport_Reference
                        ? "Truck_Receipt_No"
                        : formData.Transport_Reference_Value === "Airway Bill No."                 // ✅ was Transport_Reference
                          ? "Airway_Bill_No"
                          : "Transport_Reference";
                    handleChange(fieldName, event.target.value);
                  }}
                  size="small"
                  slotProps={{
                    input: {
                      readOnly: !isWritable,
                      endAdornment: (
                        <InputAdornment position="end" sx={{ marginRight: "10px" }}>
                          {Boolean((selectedTranche?.period_lock && isFieldLocked(
                            formData.Transport_Reference_Value === "Bill of Lading No."            // ✅ was Transport_Reference
                              ? "Split_Bill_of_Lading_No"
                              : formData.Transport_Reference_Value === "Truck Receipt No."         // ✅ was Transport_Reference
                                ? "Truck_Receipt_No"
                                : formData.Transport_Reference_Value === "Airway Bill No."         // ✅ was Transport_Reference
                                  ? "Airway_Bill_No"
                                  : "Transport_Reference",
                            "Shipment Details",
                            "Post-Shipment",
                            lockedFields
                          ))) && (
                              <Tooltip
                                title="This field is locked and cannot be edited due to period lock."
                                arrow
                                placement="top"
                              >
                                <InfoIcon
                                  fontSize="small"
                                  sx={{ cursor: 'default' }}
                                />
                              </Tooltip>
                            )}
                        </InputAdornment>
                      ),
                    },
                  }}
                />
              </Grid>
            )}

            <Grid size={{ xs: 3 }} className="form-text-field">
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <DesktopDatePicker
                    inputFormat="MM/DD/YYYY"
                    label="Shipment Date"
                    disableMaskedInput
                    readOnly={!isWritable || (selectedTranche?.period_lock && isFieldLocked("Shipment_Date", "Shipment Details", "Post-Shipment", lockedFields))}
                    className="date-picker w-100"
                    minDate={
                      (!selectedTranche || selectedTranche?.period_lock === 0) && !isCurrentTrancheUnlocked
                        ? dayjs(periodLockDate).add(1, "day")
                        : null
                    }
                    value={convertToDayJsObject(formData?.Shipment_Date)}
                    onChange={(newDate) => handleChange("Shipment_Date", newDate, true)}
                    onAccept={(newDate) => handleChange("Shipment_Date", newDate, true)}
                    enableAccessibleFieldDOMStructure={false}
                    slots={{
                      textField: (params) => (
                        <TextField
                          {...params}
                          size="small"
                          fullWidth
                          InputProps={{
                            ...params.InputProps,
                            disabled: true,
                            endAdornment: (
                              <>
                                {(selectedTranche?.period_lock || periodLockDate) && (
                                  <Tooltip
                                    title={
                                      selectedTranche?.period_lock
                                        ? "This field is locked and cannot be edited due to period lock."
                                        : `Dates before ${dayjs(periodLockDate).format("DD-MMM-YYYY")} are disabled due to period lock.`
                                    }
                                    arrow
                                    placement="top"
                                  >
                                    <InfoIcon
                                      fontSize="small"
                                      className='MuiSvgIcon-info'
                                      sx={{ marginRight: 0.5, cursor: "default" }}
                                    />
                                  </Tooltip>
                                )}
                                {params.InputProps?.endAdornment}
                              </>
                            ),
                          }}
                        />
                      ),
                    }}
                    slotProps={{
                      actionBar: {
                        actions: ["clear"]
                      }
                    }}
                  />
                </LocalizationProvider>
              </LocalizationProvider>
            </Grid>


            <Grid size={{ xs: 3 }} className="form-text-field">
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DesktopDatePicker
                  inputFormat="MM/DD/YYYY"
                  label="Delivery Date"
                  disableMaskedInput
                  readOnly={!isWritable || (selectedTranche?.period_lock && isFieldLocked("Delivery_Date", "Shipment Details", "Post-Shipment", lockedFields))}
                  className="date-picker w-100"
                  value={convertToDayJsObject(formData?.Delivery_Date)}
                  onChange={(newDate) => handleChange("Delivery_Date", newDate, true)}
                  onAccept={(newDate) => handleChange("Delivery_Date", newDate, true)}
                  enableAccessibleFieldDOMStructure={false}
                  slots={{
                    textField: (params) => (
                      <TextField
                        {...params}
                        size="small"
                        fullWidth
                        InputProps={{
                          ...params.InputProps,
                          disabled: true,
                          endAdornment: (
                            <>
                              {Boolean((selectedTranche?.period_lock && isFieldLocked("Delivery_Date", "Shipment Details", "Post-Shipment", lockedFields))) && (
                                <Tooltip
                                  title="This field is locked and cannot be edited due to period lock."
                                  arrow
                                  placement="top"
                                >
                                  <InfoIcon
                                    fontSize="small"
                                    className='MuiSvgIcon-info'
                                    sx={{ marginRight: "0", cursor: 'default' }}
                                  />
                                </Tooltip>
                              )}
                              {params.InputProps?.endAdornment}
                            </>
                          ),
                        }}
                      />
                    ),
                  }}
                  slotProps={{
                    actionBar: {
                      actions: ["clear"]
                    }
                  }}
                />
              </LocalizationProvider>
            </Grid>

            <Grid size={{ xs: 3 }} className="form-text-field">
              <TextField
                label="Insurance Ref No."
                id="Insurance_Cover_No"
                value={formData?.Insurance_Cover_No || ""}
                disabled={(selectedTranche?.period_lock && isFieldLocked("Insurance_Cover_No", "Shipment Details", "Post-Shipment", lockedFields))}
                onChange={(event) => handleChange("Insurance_Cover_No", event.target.value)}
                size="small"
                slotProps={{
                  input: {
                    readOnly: !isWritable,
                    endAdornment: (
                      <InputAdornment position="end" sx={{ marginRight: "10px" }}>
                        {Boolean((selectedTranche?.period_lock && isFieldLocked("Insurance_Cover_No", "Shipment Details", "Post-Shipment", lockedFields))) && (
                          <Tooltip
                            title="This field is locked and cannot be edited due to period lock."
                            arrow
                            placement="top"
                          >
                            <InfoIcon
                              fontSize="small"
                              sx={{ cursor: 'default' }} // No margins
                            />
                          </Tooltip>
                        )}
                      </InputAdornment>
                    ),
                  },
                }}
              />
            </Grid>

            <Grid size={{ xs: 3 }} className="form-text-field">
              <TextField
                label="Demurrage Free Days at Disport"
                type="number"
                id="Demurrage_Free_Days_at_Disport"
                disabled={(selectedTranche?.period_lock && isFieldLocked("Demurrage_Free_Days_at_Disport", "Shipment Details", "Post-Shipment", lockedFields))}
                value={formData?.Demurrage_Free_Days_at_Disport || ""}
                onChange={(event) => handleChange("Demurrage_Free_Days_at_Disport", event.target.value)}
                size="small"
                slotProps={{
                  input: {
                    readOnly: !isWritable,
                    min: 0,
                    max: 2147483647,
                    endAdornment: (
                      <InputAdornment position="end" sx={{ marginRight: "10px" }}>
                        {Boolean((selectedTranche?.period_lock && isFieldLocked("Demurrage_Free_Days_at_Disport", "Shipment Details", "Post-Shipment", lockedFields))) && (
                          <Tooltip
                            title="This field is locked and cannot be edited due to period lock."
                            arrow
                            placement="top"
                          >
                            <InfoIcon
                              fontSize="small"
                              sx={{ cursor: 'default' }} // No margins
                            />
                          </Tooltip>
                        )}
                      </InputAdornment>
                    ),
                  },
                }}
                onKeyDown={(event) => {
                  const invalidKeys = ["e", "E", "+", "-", "ArrowUp", "ArrowDown"];
                  if (invalidKeys.includes(event.key)) {
                    event.preventDefault();
                  }
                }}
                onInput={(event) => {
                  const value = event.target.value;
                  event.target.value = value.replace(/[^0-9]/g, "");
                }}
              />
            </Grid>

            <Grid size={{ xs: 3 }} className="form-text-field">
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DesktopDatePicker
                  inputFormat="MM/DD/YYYY"
                  label="Estimated Arrival to POD"
                  disableMaskedInput
                  readOnly={!isWritable || (selectedTranche?.period_lock && isFieldLocked("Estimated_Arrival_to_POD", "Shipment Details", "Post-Shipment", lockedFields))}
                  className="date-picker w-100"
                  value={convertToDayJsObject(formData?.Estimated_Arrival_to_POD)}
                  onChange={(newDate) => handleChange("Estimated_Arrival_to_POD", newDate, true)}
                  onAccept={(newDate) => handleChange("Estimated_Arrival_to_POD", newDate, true)}
                  enableAccessibleFieldDOMStructure={false}
                  slots={{
                    textField: (params) => (
                      <TextField
                        {...params}
                        size="small"
                        fullWidth
                        InputProps={{
                          ...params.InputProps,
                          disabled: true,
                          endAdornment: (
                            <>
                              {Boolean((selectedTranche?.period_lock && isFieldLocked("Estimated_Arrival_to_POD", "Shipment Details", "Post-Shipment", lockedFields))) && (
                                <Tooltip
                                  title="This field is locked and cannot be edited due to period lock."
                                  arrow
                                  placement="top"
                                >
                                  <InfoIcon
                                    fontSize="small"
                                    className='MuiSvgIcon-info'
                                    sx={{ marginRight: "0", cursor: 'default' }}
                                  />
                                </Tooltip>
                              )}
                              {params.InputProps?.endAdornment}
                            </>
                          ),
                        }}
                      />
                    ),
                  }}
                  slotProps={{
                    actionBar: {
                      actions: ["clear"]
                    }
                  }}
                />
              </LocalizationProvider>
            </Grid>

            <Grid size={{ xs: 3 }} className="form-text-field">
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DesktopDatePicker
                  inputFormat="MM/DD/YYYY"
                  label="SI Date"
                  disableMaskedInput
                  readOnly={!isWritable || (selectedTranche?.period_lock && isFieldLocked("SI_Date", "Shipment Details", "Post-Shipment", lockedFields))}
                  className="date-picker w-100"
                  value={convertToDayJsObject(formData?.SI_Date)}
                  onChange={(newDate) => handleChange("SI_Date", newDate, true)}
                  onAccept={(newDate) => handleChange("SI_Date", newDate, true)}
                  enableAccessibleFieldDOMStructure={false}
                  slots={{
                    textField: (params) => (
                      <TextField
                        {...params}
                        size="small"
                        fullWidth
                        InputProps={{
                          ...params.InputProps,
                          disabled: true,
                          endAdornment: (
                            <>
                              {Boolean((selectedTranche?.period_lock && isFieldLocked("SI_Date", "Shipment Details", "Post-Shipment", lockedFields))) && (
                                <Tooltip
                                  title="This field is locked and cannot be edited due to period lock."
                                  arrow
                                  placement="top"
                                >
                                  <InfoIcon
                                    fontSize="small"
                                    className='MuiSvgIcon-info'
                                    sx={{ marginRight: "0", cursor: 'default' }}
                                  />
                                </Tooltip>
                              )}
                              {params.InputProps?.endAdornment}
                            </>
                          ),
                        }}
                      />
                    ),
                  }}
                  slotProps={{
                    actionBar: {
                      actions: ["clear"]
                    }
                  }}
                />
              </LocalizationProvider>
            </Grid>

            <Grid size={{ xs: 3 }} className="form-text-field">
              <TextField
                label="Shipment Advice Days"
                id="Shipment_Advice_Days"
                value={formData?.Shipment_Advice_Days || ""}
                disabled={(selectedTranche?.period_lock && isFieldLocked("Shipment_Advice_Days", "Shipment Details", "Post-Shipment", lockedFields))}
                onChange={(event) => handleChange("Shipment_Advice_Days", event.target.value)}
                size="small"
                slotProps={{
                  input: {
                    readOnly: !isWritable,
                    endAdornment: (
                      <InputAdornment position="end" sx={{ marginRight: "10px" }}>
                        {Boolean((selectedTranche?.period_lock && isFieldLocked("Shipment_Advice_Days", "Shipment Details", "Post-Shipment", lockedFields))) && (
                          <Tooltip
                            title="This field is locked and cannot be edited due to period lock."
                            arrow
                            placement="top"
                          >
                            <InfoIcon
                              fontSize="small"
                              sx={{ cursor: 'default' }} // No margins
                            />
                          </Tooltip>
                        )}
                      </InputAdornment>
                    ),
                  },
                }}
              />
            </Grid>

            <Grid size={{ xs: 3 }} className="form-text-field">
              <TextField
                label="Gross Weight KGs"
                type="number"
                id="Gross_Weight_KGs"
                disabled={(selectedTranche?.period_lock && isFieldLocked("Gross_Weight_KGs", "Shipment Details", "Post-Shipment", lockedFields))}
                value={formData?.Gross_Weight_KGs || ""}
                onChange={(event) => handleChange("Gross_Weight_KGs", event.target.value)}
                size="small"
                slotProps={{
                  input: {
                    readOnly: !isWritable,
                    min: 0, max: 2147483647,
                    step: "0.001",
                    endAdornment: (
                      <InputAdornment position="end" sx={{ marginRight: "10px" }}>
                        {Boolean((selectedTranche?.period_lock && isFieldLocked("Gross_Weight_KGs", "Shipment Details", "Post-Shipment", lockedFields))) && (
                          <Tooltip
                            title="This field is locked and cannot be edited due to period lock."
                            arrow
                            placement="top"
                          >
                            <InfoIcon
                              fontSize="small"
                              sx={{ cursor: 'default' }} // No margins
                            />
                          </Tooltip>
                        )}
                      </InputAdornment>
                    ),
                  },
                }}
                onKeyDown={(event) => {
                  const invalidKeys = ["e", "E", "+", "-", "ArrowUp", "ArrowDown"];
                  if (invalidKeys.includes(event.key)) {
                    event.preventDefault();
                  }
                }}
              />
            </Grid>

            <Grid size={{ xs: 3 }} className="form-text-field">
              <TextField
                label="Net Weight KGs"
                type="number"
                id="Net_Weight_KGs"
                disabled={(selectedTranche?.period_lock && isFieldLocked("Net_Weight_KGs", "Shipment Details", "Post-Shipment", lockedFields))}
                value={formData?.Net_Weight_KGs || ""}
                onChange={(event) => handleChange("Net_Weight_KGs", event.target.value)}
                size="small"
                slotProps={{
                  input: {
                    readOnly: !isWritable,
                    min: 0, max: 2147483647,
                    step: "0.001",
                    endAdornment: (
                      <InputAdornment position="end" sx={{ marginRight: "10px" }}>
                        {Boolean((selectedTranche?.period_lock && isFieldLocked("Net_Weight_KGs", "Shipment Details", "Post-Shipment", lockedFields))) && (
                          <Tooltip
                            title="This field is locked and cannot be edited due to period lock."
                            arrow
                            placement="top"
                          >
                            <InfoIcon
                              fontSize="small"
                              sx={{ cursor: 'default' }} // No margins
                            />
                          </Tooltip>
                        )}
                      </InputAdornment>
                    ),
                  },
                }}
                onKeyDown={(event) => {
                  const invalidKeys = ["e", "E", "+", "-", "ArrowUp", "ArrowDown"];
                  if (invalidKeys.includes(event.key)) {
                    event.preventDefault();
                  }
                }}
              />
            </Grid>



            <Grid size={{ xs: 3 }} className="form-text-field">
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DesktopDatePicker
                  inputFormat="MM/DD/YYYY"
                  label="Acceptance Date to Supplier"
                  disableMaskedInput
                  readOnly={!isWritable || (selectedTranche?.period_lock && isFieldLocked("acceptance_date_from_supplier", "Shipment Details", "Post-Shipment", lockedFields))}
                  className="date-picker w-100"
                  value={convertToDayJsObject(formData?.acceptance_date_from_supplier)}
                  onChange={(newDate) => handleChange("acceptance_date_from_supplier", newDate, true)}
                  onAccept={(newDate) => handleChange("acceptance_date_from_supplier", newDate, true)}
                  enableAccessibleFieldDOMStructure={false}
                  slots={{
                    textField: (params) => (
                      <TextField
                        {...params}
                        size="small"
                        fullWidth
                        InputProps={{
                          ...params.InputProps,
                          disabled: true,
                          endAdornment: (
                            <>
                              {Boolean((selectedTranche?.period_lock && isFieldLocked("acceptance_date_from_supplier", "Shipment Details", "Post-Shipment", lockedFields))) && (
                                <Tooltip
                                  title="This field is locked and cannot be edited due to period lock."
                                  arrow
                                  placement="top"
                                >
                                  <InfoIcon
                                    fontSize="small"
                                    className='MuiSvgIcon-info'
                                    sx={{ marginRight: "0", cursor: 'default' }}
                                  />
                                </Tooltip>
                              )}
                              {params.InputProps?.endAdornment}
                            </>
                          ),
                        }}
                      />
                    ),
                  }}
                  slotProps={{
                    actionBar: {
                      actions: ["clear"]
                    }
                  }}
                />
              </LocalizationProvider>
            </Grid>

            <Grid size={{ xs: 3 }} className="form-text-field">
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DesktopDatePicker
                  inputFormat="MM/DD/YYYY"
                  label="Acceptance Date from Customer"
                  disableMaskedInput
                  readOnly={!isWritable || (selectedTranche?.period_lock && isFieldLocked("acceptance_date_to_customer", "Shipment Details", "Post-Shipment", lockedFields))}
                  className="date-picker w-100"
                  value={convertToDayJsObject(formData?.acceptance_date_to_customer)}
                  onChange={(newDate) => handleChange("acceptance_date_to_customer", newDate, true)}
                  onAccept={(newDate) => handleChange("acceptance_date_to_customer", newDate, true)}
                  enableAccessibleFieldDOMStructure={false}
                  slots={{
                    textField: (params) => (
                      <TextField
                        {...params}
                        size="small"
                        fullWidth
                        InputProps={{
                          ...params.InputProps,
                          disabled: true,
                          endAdornment: (
                            <>
                              {Boolean((selectedTranche?.period_lock && isFieldLocked("acceptance_date_to_customer", "Shipment Details", "Post-Shipment", lockedFields))) && (
                                <Tooltip
                                  title="This field is locked and cannot be edited due to period lock."
                                  arrow
                                  placement="top"
                                >
                                  <InfoIcon
                                    fontSize="small"
                                    className='MuiSvgIcon-info'
                                    sx={{ marginRight: "0", cursor: 'default' }}
                                  />
                                </Tooltip>
                              )}
                              {params.InputProps?.endAdornment}
                            </>
                          ),
                        }}
                      />
                    ),
                  }}
                  slotProps={{
                    actionBar: {
                      actions: ["clear"]
                    }
                  }}
                />
              </LocalizationProvider>
            </Grid>


            <Grid size={{ xs: 3 }} className="form-text-field">
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DesktopDatePicker
                  inputFormat="MM/DD/YYYY"
                  label="Supplier Payment Maturity Date"
                  disableMaskedInput
                  readOnly
                  disabled
                  className="date-picker w-100"
                  value={convertToDayJsObject(formData?.supplier_payment_maturity_date)}
                  enableAccessibleFieldDOMStructure={false}
                  slots={{
                    textField: (params) => (
                      <TextField
                        {...params}
                        size="small"
                        fullWidth
                        InputProps={{
                          ...params.InputProps,
                          disabled: true,
                          endAdornment: (
                            <>
                              <Tooltip
                                title="Supplier Maturity Date is automatically calculated based on the Buying payment terms."
                                arrow
                                placement="top"
                              >
                                <InfoIcon
                                  fontSize="small"
                                  className="MuiSvgIcon-info"
                                  sx={{ marginRight: 0.5, cursor: "default" }}
                                />
                              </Tooltip>
                              {params.InputProps?.endAdornment}
                            </>
                          ),
                        }}
                      />
                    ),
                  }}
                  slotProps={{
                    actionBar: {
                      actions: []
                    }
                  }}
                />
              </LocalizationProvider>
            </Grid>

            <Grid size={{ xs: 3 }} className="form-text-field">
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DesktopDatePicker
                  inputFormat="MM/DD/YYYY"
                  label="Customer Payment Maturity Date"
                  disableMaskedInput
                  readOnly
                  disabled
                  className="date-picker w-100"
                  value={convertToDayJsObject(formData?.customer_payment_maturity_date)}
                  enableAccessibleFieldDOMStructure={false}
                  slots={{
                    textField: (params) => (
                      <TextField
                        {...params}
                        size="small"
                        fullWidth
                        InputProps={{
                          ...params.InputProps,
                          disabled: true,
                          endAdornment: (
                            <>
                              <Tooltip
                                title="Maturity Date is automatically calculated based on the Selling payment terms."
                                arrow
                                placement="top"
                              >
                                <InfoIcon
                                  fontSize="small"
                                  className="MuiSvgIcon-info"
                                  sx={{ marginRight: 0.5, cursor: "default" }}
                                />
                              </Tooltip>
                              {params.InputProps?.endAdornment}
                            </>
                          ),
                        }}
                      />
                    ),
                  }}
                  slotProps={{
                    actionBar: {
                      actions: []
                    }
                  }}
                />
              </LocalizationProvider>
            </Grid>


            <Grid size={{ xs: 4 }} className="form-text-field">
              <TextField
                label="Insurance Company Details"
                id="Insurance_Company_Details"
                multiline
                disabled={(selectedTranche?.period_lock && isFieldLocked("Insurance_Company_Details", "Shipment Details", "Post-Shipment", lockedFields))}
                rows={3}
                value={formData?.Insurance_Company_Details || ""}
                onChange={(event) => handleChange("Insurance_Company_Details", event.target.value)}
                size="small"
                slotProps={{
                  input: {
                    readOnly: !isWritable,
                    endAdornment: (
                      <InputAdornment position="end" sx={{ marginRight: "10px" }}>
                        {Boolean((selectedTranche?.period_lock && isFieldLocked("Insurance_Company_Details", "Shipment Details", "Post-Shipment", lockedFields))) && (
                          <Tooltip
                            title="This field is locked and cannot be edited due to period lock."
                            arrow
                            placement="top"
                          >
                            <InfoIcon
                              fontSize="small"
                              sx={{ cursor: 'default' }} // No margins
                            />
                          </Tooltip>
                        )}
                      </InputAdornment>
                    ),
                  },
                }}
              />
            </Grid>
          </Grid>
        </Grid>
      )
    },
    {
      id: 3,
      title: "SI Creation",
      content: (
        <Grid size={{ xs: 12 }}>
          <Grid
            container
            spacing={"10px"}
            className="align-items-start autocomplete"
          >
            <Grid size={{ xs: 3 }} className="form-text-field">
              <TextField
                label="Shipper"
                id="Shipper"
                value={formData?.Shipper || ""}
                disabled={(selectedTranche?.period_lock && isFieldLocked("Shipper", "Shipment Details", "Post-Shipment", lockedFields))}
                onChange={(event) => handleChange("Shipper", event.target.value)}
                size="small"
                slotProps={{
                  input: {
                    readOnly: !isWritable,
                    endAdornment: (
                      <InputAdornment position="end" sx={{ marginRight: "10px" }}>
                        {Boolean((selectedTranche?.period_lock && isFieldLocked("Insurance_Company_Details", "Shipment Details", "Post-Shipment", lockedFields))) && (
                          <Tooltip
                            title="This field is locked and cannot be edited due to period lock."
                            arrow
                            placement="top"
                          >
                            <InfoIcon
                              fontSize="small"
                              sx={{ cursor: 'default' }} // No margins
                            />
                          </Tooltip>
                        )}
                      </InputAdornment>
                    ),
                  },
                }}
              />
            </Grid>

            <Grid size={{ xs: 3 }} className="form-text-field">
              <TextField
                label="Notify Customer"
                id="Notify_Customer"
                value={formData?.Notify_Customer || ""}
                disabled={(selectedTranche?.period_lock && isFieldLocked("Notify_Customer", "Shipment Details", "Post-Shipment", lockedFields))}
                onChange={(event) => handleChange("Notify_Customer", event.target.value)}
                size="small"
                slotProps={{
                  input: {
                    readOnly: !isWritable,
                    endAdornment: (
                      <InputAdornment position="end" sx={{ marginRight: "10px" }}>
                        {Boolean((selectedTranche?.period_lock && isFieldLocked("Insurance_Company_Details", "Shipment Details", "Post-Shipment", lockedFields))) && (
                          <Tooltip
                            title="This field is locked and cannot be edited due to period lock."
                            arrow
                            placement="top"
                          >
                            <InfoIcon
                              fontSize="small"
                              sx={{ cursor: 'default' }} // No margins
                            />
                          </Tooltip>
                        )}
                      </InputAdornment>
                    ),
                  },
                }}
              />
            </Grid>

            <Grid size={{ xs: 3 }} className="form-text-field">
              <TextField
                label="Final Destination"
                id="Final_Destination"
                value={formData?.Final_Destination || ""}
                disabled={(selectedTranche?.period_lock && isFieldLocked("Final_Destination", "Shipment Details", "Post-Shipment", lockedFields))}
                onChange={(event) => handleChange("Final_Destination", event.target.value)}
                size="small"
                slotProps={{
                  input: {
                    readOnly: !isWritable,
                    endAdornment: (
                      <InputAdornment position="end" sx={{ marginRight: "10px" }}>
                        {Boolean((selectedTranche?.period_lock && isFieldLocked("Insurance_Company_Details", "Shipment Details", "Post-Shipment", lockedFields))) && (
                          <Tooltip
                            title="This field is locked and cannot be edited due to period lock."
                            arrow
                            placement="top"
                          >
                            <InfoIcon
                              fontSize="small"
                              sx={{ cursor: 'default' }} // No margins
                            />
                          </Tooltip>
                        )}
                      </InputAdornment>
                    ),
                  },
                }}
              />
            </Grid>

            <Grid size={{ xs: 3 }}>
              <VirtualizedAutocomplete
                isMultiple={false}
                isObject={false}
                isWritable={isWritable}
                isLocked={Boolean(selectedTranche?.period_lock && isFieldLocked("Transhipment", "Shipment Details", "Post-Shipment", lockedFields))}
                filterOn="Transhipment"
                options={partialShipments}
                selectedOptions={formData?.Transhipment || null}
                handleSelectChange={(filterOn, newValue) => handleChange("Transhipment", newValue)}
              />
            </Grid>

            <Grid size={{ xs: 3 }} className="form-text-field">
              <TextField
                label="Notify Bank"
                id="Notify_Bank"
                value={formData?.Notify_Bank || ""}
                disabled={(selectedTranche?.period_lock && isFieldLocked("Notify_Bank", "Shipment Details", "Post-Shipment", lockedFields))}
                onChange={(event) => handleChange("Notify_Bank", event.target.value)}
                size="small"
                slotProps={{
                  input: {
                    readOnly: !isWritable,
                    endAdornment: (
                      <InputAdornment position="end" sx={{ marginRight: "10px" }}>
                        {Boolean((selectedTranche?.period_lock && isFieldLocked("Insurance_Company_Details", "Shipment Details", "Post-Shipment", lockedFields))) && (
                          <Tooltip
                            title="This field is locked and cannot be edited due to period lock."
                            arrow
                            placement="top"
                          >
                            <InfoIcon
                              fontSize="small"
                              sx={{ cursor: 'default' }} // No margins
                            />
                          </Tooltip>
                        )}
                      </InputAdornment>
                    ),
                  },
                }}
              />
            </Grid>

            <Grid size={{ xs: 3 }} className="form-text-field">
              <TextField
                label="Consignee"
                id="Consignee"
                value={formData?.Consignee || ""}
                disabled={(selectedTranche?.period_lock && isFieldLocked("Consignee", "Shipment Details", "Post-Shipment", lockedFields))}
                onChange={(event) => handleChange("Consignee", event.target.value)}
                size="small"
                slotProps={{
                  input: {
                    readOnly: !isWritable,
                    endAdornment: (
                      <InputAdornment position="end" sx={{ marginRight: "10px" }}>
                        {Boolean((selectedTranche?.period_lock && isFieldLocked("Consignee", "Shipment Details", "Post-Shipment", lockedFields))) && (
                          <Tooltip
                            title="This field is locked and cannot be edited due to period lock."
                            arrow
                            placement="top"
                          >
                            <InfoIcon
                              fontSize="small"
                              sx={{ cursor: 'default' }} // No margins
                            />
                          </Tooltip>
                        )}
                      </InputAdornment>
                    ),
                  },
                }}
              />
            </Grid>

            <Grid size={{ xs: 3 }}>
              <VirtualizedAutocomplete
                isMultiple={false}
                isObject={false}
                isWritable={isWritable}
                isLocked={Boolean(selectedTranche?.period_lock && isFieldLocked("Partial_Shipment", "Shipment Details", "Post-Shipment", lockedFields))}
                filterOn="Partial Shipment"
                options={partialShipments}
                selectedOptions={formData?.Partial_Shipment || null}
                handleSelectChange={(filterOn, newValue) => handleChange("Partial_Shipment", newValue)}
              />
            </Grid>
          </Grid>
        </Grid>
      )
    },
    {
      id: 4,
      title: "Other Certificates",
      content: (
        <Grid size={{ xs: 12 }}>
          <Grid
            container
            spacing={"10px"}
            className="align-items-start autocomplete"
          >
            <Grid size={{ xs: 12 }} className="mb-3">
              {formData?.Certificates_of_Origin?.map((certificate, index) => (
                <Accordion
                  ref={(element) => (certificateAccordionRefs.current[index] = element)}
                  key={index}
                  expanded={isCertificateAccodionExpanded === index}
                  onChange={handleAccordionChange(index, "Certificates")}
                >
                  <AccordionSummary
                    id={`product-header-${index}`}
                    aria-controls={`product-content-${index}`}
                    expandIcon={<ExpandMoreIcon />}
                    slot="div"
                  >
                    <small className="page-heading fw-bold item">Other Certificate {index + 1}</small>
                    <IconButton
                      color="error"
                      size="small"
                      className="pt-0"
                      disabled={!isWritable}
                      onClick={(event) => handleDeleteCertificate(event, index)}
                    >
                      <DeleteIcon fontSize="inherit" />
                    </IconButton>
                  </AccordionSummary>
                  <AccordionDetails>
                    <div className="row w-100 flex-column align-items-start gap-3">
                      <div className="col-3 form-text-field p-0">
                        <TextField
                          label="Title"
                          id="COO_Title"
                          value={certificate.COO_Title || ""}
                          disabled={(selectedTranche?.period_lock && isFieldLocked("COO_Title", "Shipment Details", "Post-Shipment", lockedFields))}
                          onChange={(event) => handleCertificateChange(index, "COO_Title", event.target.value)}
                          slotProps={{
                            input: {
                              readOnly: !isWritable
                            }
                          }}
                        />
                      </div>

                      <div className="col-6 form-text-field p-0">
                        <TextField
                          label="Content"
                          id="COO_Content"
                          multiline
                          disabled={(selectedTranche?.period_lock && isFieldLocked("COO_Content", "Shipment Details", "Post-Shipment", lockedFields))}
                          rows={3}
                          value={certificate.COO_Content || ""}
                          onChange={(event) => handleCertificateChange(index, "COO_Content", event.target.value)}
                          slotProps={{
                            input: {
                              readOnly: !isWritable
                            }
                          }}
                        />
                      </div>
                    </div>
                  </AccordionDetails>
                </Accordion>
              ))}
            </Grid>
          </Grid>
        </Grid>
      )
    }
  ];

  return (
    <>
      <form
        ref={formRef}
        className="deal-form multi-form-container"
        onSubmit={handleSubmit}
      >
        <div className="d-flex flex-column gap-2">
          {formSectionsHTML.map((section) => (
            <Accordion
              key={section.id}
              expanded={expandedFormSections.includes(section.id)}
              onChange={handleSectionAccordionChange(section.id)}
            >
              <AccordionSummary
                id="panel-header"
                aria-controls="panel-content"
                expandIcon={<ExpandMoreIcon />}
                slot="div"
              >
                <small className="page-heading title">
                  {section.title.includes("Product") ? `Product${isFoodsOrPolymersDeal ? " Grade" : ""}s` : section.title}
                </small>

                {expandedFormSections.includes(section.id) && (
                  <>
                    {(section.title.includes("Product") && !isSBLCDeal) ? (
                      <IconButton
                        color="primary"
                        disabled={!isWritable || selectedTranche?.period_lock}
                        onClick={isFoodsOrPolymersDeal ? handleAddProductGrade : handleAddProduct}
                      >
                        <AddBoxIcon
                          fontSize="large"
                          disabled={selectedTranche?.period_lock}
                          sx={{ color: '#0C969C' }}
                        />
                      </IconButton>
                    ) : section.title === "Other Certificates" && (
                      <IconButton
                        color="primary"
                        disabled={!isWritable}
                        onClick={handleAddCertificate}
                      >
                        <AddBoxIcon
                          fontSize="large"
                          sx={{ color: '#0C969C' }}
                        />
                      </IconButton>
                    )}
                  </>
                )}
              </AccordionSummary>
              <AccordionDetails>
                <Grid
                  container
                  spacing={"10px"}
                  className="w-100 align-items-start autocomplete"
                >
                  {section.content}
                </Grid>
              </AccordionDetails>
            </Accordion>
          ))}
        </div>
      </form>

      <Dialog open={showWarningDialog} onClose={handleCloseDialog}>
        <DialogContent>
          <DialogContentText className="text-center">
            Please fill in at least one field in the form before adding a new entry.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <button
            type="button"
            className="btn btn-primary custom-btn"
            onClick={handleCloseDialog}
          >
            Ok
          </button>
        </DialogActions>
      </Dialog>

      <Modal show={showAddModal} onHide={handleCloseModal} centered>
        <Modal.Header style={{ padding: '7px 16px' }} closeButton>
          <Modal.Title>Are you sure?</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <span>
            Adding a new form will discard your unsaved changes. Proceed
            anyway?
          </span>
        </Modal.Body>
        <Modal.Footer className="gap-2">
          <Button variant="outlined" size="small" onClick={handleCloseModal}>
            Cancel
          </Button>
          <Button variant="contained" size="small" onClick={handleAddTranche}>
            Add
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
});

export default memo(ShipmentDetails);